/**
 * @Description 评分概要(包含预测营业额) 四维评分时使用
 */

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from 'antd';
import { isNotEmpty, isNotEmptyAny } from '@lhb/func';
import cs from 'classnames';
import IconFont from '@/common/components/IconFont';
import SiteMapExplainExtra from '@/common/components/business/SiteMap/SiteMapExplainExtra';
import V2Predict from '@/common/components/business/AreaSalesPredict/V2Predict';
import { markTypeEnum } from '@/views/iterate/pages/siteselectionmapb/ts-config';
import styles from './index.module.less';
import { getClusterScores, getShareClusterScores } from '@/common/api/youth';
import { fullEditorCss } from '@/common/components/config-v2';
const { Text } = Typography;

interface ScoreSummaryProps {
  overviewData: any;
  predictSale: boolean;
  configSwitch: any;
  shareParams?:Object;// 分享页数据
  /** 是否是pdf */
  isPdf?:boolean;
  modelConfigId?:any;
  isSemirModel?: boolean; // 是否是森马模型
  noCheckExtraScore?: boolean; // 是否不去调用额外评分接口,点位测评街边店不需要调用
  detail?: any;
}
const ScoreSummaryHorizontal: FC<ScoreSummaryProps> = ({
  overviewData,
  predictSale,
  configSwitch,
  shareParams = {},
  isPdf = false,
  modelConfigId,
  isSemirModel = false,
  noCheckExtraScore = false,
  detail,
}) => {
  const shadowRef: any = useRef();
  const [descriptions, setDescriptions] = useState<any>(null);

  const evaluateInfoStr = useMemo(() => {
    const { marketScore, firstLevelName } = overviewData || {};
    if (marketScore > 70) {
      return `有利于${firstLevelName}经营`;
    }
    if (marketScore > 50 && marketScore <= 70) {
      return `经营${firstLevelName}一般`;
    }
    if (marketScore <= 50) {
      return `不利于${firstLevelName}经营`;
    }
    return '-';
  }, [overviewData]);


  // 四维评分时弹框中展示的评分文案
  const extraParagraph = useMemo(() => {
    const { markType } = overviewData || {};
    if (markTypeEnum.four !== markType) return null;
    if (!descriptions) return null;
    const parag = {
      title: '评分说明',
      descriptions
    };
    return parag;
  }, [overviewData, descriptions]);


  // 初始化时获取评分说明
  useEffect(() => {
    if (noCheckExtraScore) return;
    if (markTypeEnum.four !== overviewData?.markType) return;
    const api = Object.keys(shareParams)?.length ? getShareClusterScores : getClusterScores;
    api({
      id: overviewData?.id,
      modelConfigId: modelConfigId
    }).then((scores) => {
      const descriptionsInfo = scores.filter(itm => !!itm.name && !!itm.msg).map(itm => `${itm.name}：${itm.msg}`);
      setDescriptions(descriptionsInfo);
    });
  }, [overviewData]);

  const showScoreDescriptionFlag = useMemo(() => {
    // 1：默认值，2：自定义富文本，3：不展示
    // 如果没有这个参数，也就是没设置过，就沿用老逻辑展示默认值。
    if (!isNotEmpty(detail.showScoreDescriptionFlag)) {
      return 1;
    } else {
      return detail.showScoreDescriptionFlag;
    }
  }, [detail.showScoreDescriptionFlag]);
  return (
    <div className={styles.scoreSummaryHorizontal}>
      <div className={styles.firstBlock}>
        <div className={styles.scoreTitle}>评分</div>
        <div className={styles.totalScoreInfo}>
          <div className={cs(styles.totalScoreVal, 'mr-4')}>{overviewData?.marketScore}</div>
          {[1, 2].includes(showScoreDescriptionFlag) && !isPdf && !isSemirModel && <div>
            <IconFont
              iconHref='iconxq_ic_shuoming_normal'
              className='mr-2 c-666 fs-12'
              tooltipConfig={{
                placement: 'bottom',
                overlayInnerStyle: {
                  width: '380px',
                  padding: '16px',
                  boxSizing: 'border-box',
                  borderRadius: '4px',
                  ...(extraParagraph ? {
                    maxHeight: 'calc(50vh)',
                    overflowY: 'auto',
                  } : {})
                },
                color: '#fff',
                onOpenChange(open) {
                  // left: 737px; top: 137px; transform-origin: 50% -4px;
                  if (showScoreDescriptionFlag !== 1 && detail.scoreDescription && open) {
                    setTimeout(() => {
                      // 创建Shadow DOM根节点
                      const shadowRoot = shadowRef.current.attachShadow({ mode: 'open' });
                      const contentDiv = document.createElement('div');
                      contentDiv.innerHTML = detail.scoreDescription;
                      // 创建样式
                      const style = document.createElement('style');
                      style.textContent = fullEditorCss;
                      // 添加到Shadow DOM
                      shadowRoot.appendChild(style);
                      shadowRoot.appendChild(contentDiv);
                    }, 1);
                  }
                },
                title: showScoreDescriptionFlag === 1 ? (
                  <SiteMapExplainExtra
                    markType={overviewData?.markType}
                    isPredictSale={predictSale}
                    shareParams={shareParams}
                    extraParagraphs={extraParagraph ? [extraParagraph] : []}
                  />
                ) : (
                  <div ref={shadowRef}></div>
                ),
              }}
            />
          </div>}
          { configSwitch?.showIndustryBenefitContent
            ? <div className='c-222 fs-12'>
              <Text
                style={predictSale || isNotEmptyAny(overviewData.rankingText) ? { maxWidth: '150px' } : { maxWidth: '250px' }}
                ellipsis={{ tooltip: evaluateInfoStr }}
                className='c-222 fs-12'
              >
                <span>
                    ({evaluateInfoStr})
                </span>
              </Text>
            </div>
            : <></> }
        </div>
      </div>
      {/* 预测营业额 */}
      {
        predictSale ? <div className={styles.forecastSection}>
          <V2Predict
            data={overviewData}
            style={{
              height: '50px',
              backgroundImage: 'none',
              backgroundColor: '#ffffffb3'
            }}
          />
          {
            isNotEmptyAny(overviewData.rankingText) ? <div className={cs(styles.comprehensiveRankCon, 'ml-8')}>
              <div className='c-222'>
                <span>市内前</span>
                <span className={styles.rankText}>{overviewData.rankingText}</span>
              </div>
              <div className='c-999 mt-2'>
                综合评分
              </div>
            </div> : <></>
          }
        </div> : <>
          { isNotEmptyAny(overviewData.rankingText) ? <div className={styles.comprehensiveRankConIndividual}>
            <div className='c-999'>综合评分</div>
            <div className='ml-4'>
              <span className='c-222'>市内前</span>
              <span className={cs('c-222', styles.rankText)}>{overviewData.rankingText}</span>
            </div>
          </div> : <></>}
        </>
      }
      <div className={styles.rightIcon}>
        <img src={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/selectionmap/icon_score.png`} width='100%' height='100%'/>
      </div>
    </div>
  );
};

export default ScoreSummaryHorizontal;
